/* eslint-disable no-await-in-loop */
/**
 * Secure Upload Manager
 *
 * This upload manager uses presigned URLs from the upload service instead of
 * direct AWS SDK calls with hardcoded credentials. It maintains feature parity
 * with uploadManagerNew.js but with improved security.
 *
 * Key Features:
 * - Presigned URL uploads (no AWS credentials in client)
 * - Concurrent upload queue (max 3 active uploads)
 * - Pause/resume with state persistence
 * - Network monitoring and auto-resume
 * - Progress smoothing and ETA calculation
 * - WebSocket server-side progress tracking
 * - Retry logic with exponential backoff
 */

import { toast } from 'react-toastify';
import { UPLOAD_CONFIG } from '../Config/uploadConfig';
import { markSectionExplored, EXPLORED_SECTION } from './onboardingChecklist';
import { logAppEvent } from '../Api/errorLogger';
import {
  createUploadSession,
  completeUploadSession,
  uploadPartWithPresignedUrl,
  registerDocument,
  refreshPresignedUrls,
  createGiftUploadSession,
  completeGiftUploadSession,
  refreshGiftPresignedUrls,
} from '../Api/uploaderApiFunctions';
import { getUserUuid } from '../Api/authHelpers';

// Upload states (same as uploadManagerNew.js)
export const UPLOAD_STATUS = {
  PENDING: 'pending',
  INITIALIZING: 'initializing',
  UPLOADING: 'uploading',
  PAUSED: 'paused',
  FINISHING: 'finishing',
  COMPLETED: 'completed',
  S3_COMPLETE: 's3_complete', // Gift flow: S3 upload done, awaiting payment for registration
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  CANCELLING: 'cancelling',
  NETWORK_ERROR: 'network_error',
};

/**
 * Helper function to create dummy object for display during upload
 */
const createDummy = (options) => {
  const {
    draft_id,
    title,
    fileName,
    thumbnail,
    ForNoImage,
    category,
    description,
    videoSize,
    selectedGroups,
    changeSize,
  } = options;

  const cleanThumbnail = thumbnail
    ? thumbnail.replace(/,+$/, '').trim()
    : ForNoImage;

  return {
    draft_id,
    title: title || fileName,
    thumbnail: cleanThumbnail,
    category,
    video_description: description,
    video_size: changeSize ? changeSize(videoSize) : videoSize,
    type: 0,
    group_id: selectedGroups,
    content_status: 'Video',
    uploadIsTrying: 1,
  };
};

/**
 * Generate S3 key matching mobile app format
 *
 * Uses User UUID from api_response.uuid (preferred) or falls back to userId.
 * Mobile app uses UUID for S3 paths: wasabi_v2_mobile.dart:47-50
 *
 * @param {string} userId - User ID (fallback)
 * @param {string} filename - Original filename
 * @returns {string} S3 key: cloud/production/{uuid}/{timestamp}_{filename}
 */
function generateSecureS3Key(userId, filename) {
  const timestamp = Date.now();
  const cleanFilename = filename.replace(/[^a-zA-Z0-9._-]/g, '_');

  // Determine environment
  const env = window.location.origin.includes('preprod')
    ? 'pre-production'
    : 'production';

  // Prefer UUID over userId (matches mobile app)
  const userUuid = getUserUuid();
  const userIdentifier = userUuid || userId;

  if (!userUuid) {
    console.warn('[SecureUpload] User UUID not found, using userId as fallback');
  }

  return `cloud/${env}/${userIdentifier}/${timestamp}_${cleanFilename}`;
}

/**
 * Enhanced Upload Manager with Presigned URLs
 */
class UploadManagerSecure {
  constructor() {
    this.uploads = new Map(); // uploadId -> UploadTask
    this.listeners = new Set();
    this.networkStatus = 'online';
    this.networkCheckInterval = null;
    this.consecutiveNetworkFailures = 0;
    // Throttle notifyListeners so high-frequency progress events don't re-render
    // every subscriber on every tick. Leading + trailing edge: status changes
    // emit immediately, progress floods are coalesced to one emit per window.
    this.notifyDelay = 250;
    this.notifyLastEmit = 0;
    this.notifyTimer = null;
    this.startNetworkMonitoring();
  }

  /**
   * Get count of active uploads
   */
  getActiveUploadCount() {
    let count = 0;
    this.uploads.forEach((task) => {
      // Remote (mobile) uploads are tracking-only — they don't use local upload slots
      if (task.uploadType === 'remote') return;
      if (
        task.status === UPLOAD_STATUS.UPLOADING
        || task.status === UPLOAD_STATUS.INITIALIZING
        || task.status === UPLOAD_STATUS.FINISHING
      ) {
        count += 1;
      }
    });
    return count;
  }

  /**
   * Get total pending items count
   */
  getPendingItemsCount() {
    let totalPending = 0;

    this.uploads.forEach((task) => {
      const isActive = task.status === UPLOAD_STATUS.PENDING
        || task.status === UPLOAD_STATUS.INITIALIZING
        || task.status === UPLOAD_STATUS.UPLOADING
        || task.status === UPLOAD_STATUS.PAUSED
        || task.status === UPLOAD_STATUS.NETWORK_ERROR
        || task.status === UPLOAD_STATUS.FINISHING;

      if (isActive) {
        if (task.uploadType === 'photo') {
          const remaining = (task.totalPhotos || 0) - (task.uploadedPhotos || 0);
          totalPending += remaining;
        } else {
          totalPending += 1;
        }
      }
    });

    return totalPending;
  }

  /**
   * Get currently uploading items count
   */
  getCurrentlyUploadingCount() {
    let totalUploading = 0;

    this.uploads.forEach((task) => {
      const isActive = task.status === UPLOAD_STATUS.PENDING
        || task.status === UPLOAD_STATUS.UPLOADING
        || task.status === UPLOAD_STATUS.INITIALIZING
        || task.status === UPLOAD_STATUS.FINISHING
        || task.status === UPLOAD_STATUS.PAUSED
        || task.status === UPLOAD_STATUS.NETWORK_ERROR;

      if (isActive) {
        if (task.uploadType === 'photo') {
          const remaining = (task.totalPhotos || 0) - (task.uploadedPhotos || 0);
          totalUploading += remaining;
        } else {
          totalUploading += 1;
        }
      }
    });

    return totalUploading;
  }

  /**
   * Start next upload in queue if slot available
   */
  startNextInQueue() {
    const activeCount = this.getActiveUploadCount();

    if (activeCount >= UPLOAD_CONFIG.MAX_CONCURRENT_UPLOADS) {
      return;
    }

    let nextUpload = null;
    this.uploads.forEach((task) => {
      if (!nextUpload && task.status === UPLOAD_STATUS.PENDING) {
        nextUpload = task;
      }
    });

    if (nextUpload) {
      console.log(`[SecureUpload] Starting queued upload: ${nextUpload.fileName}`);
      this.startUpload(nextUpload.id);
    }
  }

  /**
   * Start monitoring network status
   */
  startNetworkMonitoring() {
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));

    // Device wake / tab foreground recovery. On mobile a screen lock or app
    // switch does NOT fire 'offline' (the network wasn't lost — the tab was
    // suspended), so the 'online' handler never runs. When we come back to
    // the foreground we re-check connectivity and resume anything that stalled
    // while we were backgrounded. This is the primary fix for "locked the
    // phone and the upload never continued".
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.checkNetworkStatus();
          this.recoverStalledUploads();
        }
      });
    }
    window.addEventListener('focus', () => {
      this.checkNetworkStatus();
      this.recoverStalledUploads();
    });

    this.networkCheckInterval = setInterval(() => {
      this.checkNetworkStatus();
      // Safety net: pick up anything parked in NETWORK_ERROR (or an uploading
      // task whose loop died) even if no online/visibility event fired.
      if (this.networkStatus === 'online') this.recoverStalledUploads();
    }, UPLOAD_CONFIG.NETWORK_CHECK_INTERVAL);
  }

  /**
   * Abort the in-flight part request for a task (if any) so an awaiting upload
   * loop rejects promptly instead of hanging on a dead socket.
   */
  // eslint-disable-next-line class-methods-use-this
  abortActiveXhr(task) {
    if (task && task.activeXhrRef && task.activeXhrRef.current) {
      try { task.activeXhrRef.current.abort(); } catch (e) { /* noop */ }
      // eslint-disable-next-line no-param-reassign
      task.activeXhrRef = null;
    }
  }

  /**
   * Re-issue presigned URLs for parts [fromPartNumber .. totalParts] and merge
   * them into task.presignedUrls. S3 presigned URLs are short-lived; when a tab
   * is frozen during a screen lock (notably Android Chrome, which suspends
   * background tabs harder than iOS Safari) the URLs can expire before we get
   * to resume, and uploading to an expired URL returns 403. Refreshing keeps
   * the resume working instead of hard-failing.
   */
  // eslint-disable-next-line class-methods-use-this
  async refreshTaskUrls(task, fromPartNumber) {
    const partNumbers = [];
    for (let p = fromPartNumber; p <= task.totalParts; p += 1) partNumbers.push(p);
    console.log(`[SecureUpload] Refreshing presigned URLs from part ${fromPartNumber} for ${task.fileName}`);
    const res = task.giftUuid
      ? await refreshGiftPresignedUrls(task.sessionId, task.giftUuid, partNumbers)
      : await refreshPresignedUrls(task.sessionId, partNumbers);
    const fresh = (res && res.presigned_urls) || [];
    if (fresh.length) {
      const byNum = new Map(
        (task.presignedUrls || []).map((u) => [u.part_number ?? u.partNumber, u]),
      );
      fresh.forEach((u) => byNum.set(u.part_number ?? u.partNumber, u));
      // eslint-disable-next-line no-param-reassign
      task.presignedUrls = Array.from(byNum.values());
    }
  }

  /**
   * Return a usable presigned URL for `partNumber`, refreshing from the server
   * first if the cached one is missing or within the expiry safety margin.
   */
  async getFreshPartUrl(task, partNumber) {
    const lookup = () => (task.presignedUrls || []).find(
      (u) => u.part_number === partNumber || u.partNumber === partNumber,
    );
    let obj = lookup();
    const marginMs = 120000; // refresh if expiring within 2 minutes
    const expiresMs = obj && obj.expires_at ? new Date(obj.expires_at).getTime() : null;
    const expired = expiresMs !== null && expiresMs <= Date.now() + marginMs;
    if (!obj || expired) {
      await this.refreshTaskUrls(task, partNumber);
      obj = lookup();
    }
    return obj ? (obj.presigned_url || obj.url) : null;
  }

  /**
   * Resume uploads that parked on a network error, and re-kick any task that
   * believes it's uploading but has no live loop (a loop that died silently
   * while the tab was frozen). Safe to call repeatedly — the isRunning guard
   * in startUpload prevents duplicate loops.
   */
  recoverStalledUploads() {
    if (this.networkStatus === 'offline' || !navigator.onLine) return;
    /* eslint-disable no-param-reassign */
    this.uploads.forEach((task) => {
      // Only the generic multipart pipeline (startUpload's session/part-loop
      // machinery) can have its JS loop die silently while the tab was
      // frozen — that's what this recovery is for. Photo uploads run their
      // own independent axios loop (PhotoUploadModal / UploadDropdown) and
      // never set isRunning, so every photo task's normal UPLOADING status
      // looked "stalled" here and got funnelled into startUpload(), which
      // immediately threw (no task.file/sessionId on a photo task) and
      // flashed the item to FAILED right before the real photo loop finished
      // and marked it COMPLETED. Remote (mobile) uploads are tracking-only
      // for the same reason.
      if (task.uploadType !== 'video' || task.isRunning) return;
      if (task.status === UPLOAD_STATUS.NETWORK_ERROR) {
        task.status = UPLOAD_STATUS.UPLOADING;
        task.isPaused = false;
        this.notifyListeners();
        this.startUpload(task.id);
      } else if (task.status === UPLOAD_STATUS.UPLOADING) {
        // Marked uploading but no loop is running → restart from the last
        // completed part.
        this.startUpload(task.id);
      }
    });
    /* eslint-enable no-param-reassign */
  }

  handleOnline() {
    console.log('[SecureUpload] Network: ONLINE');
    this.networkStatus = 'online';
    this.consecutiveNetworkFailures = 0;
    this.notifyListeners();

    // Auto-resume uploads that parked on a network error. Mutate the task in
    // place (NOT spread-into-a-new-object): a running upload loop holds a
    // reference to this exact object and reads task.isPaused/status/
    // currentPartNumber off it — replacing the map entry would orphan the
    // loop from the flags meant to control it.
    /* eslint-disable no-param-reassign */
    this.uploads.forEach((task) => {
      if (task.status === UPLOAD_STATUS.NETWORK_ERROR && !task.isRunning) {
        console.log(`[SecureUpload] Auto-resuming upload ${task.id} after network restore`);
        if (!task.sessionId && task.currentPartNumber === 0) {
          task.status = UPLOAD_STATUS.PENDING;
          task.isPaused = false;
          this.notifyListeners();
          this.startNextInQueue();
        } else {
          this.resumeUpload(task.id);
        }
      }
    });
    /* eslint-enable no-param-reassign */
  }

  handleOffline() {
    console.log('[SecureUpload] Network: OFFLINE');
    this.networkStatus = 'offline';
    this.notifyListeners();

    // Auto-pause active uploads AND abort the in-flight part request. The
    // abort is essential: without it the awaiting loop hangs on a dead socket
    // (no load/error ever fires), so just flipping a status flag does nothing.
    /* eslint-disable no-param-reassign */
    this.uploads.forEach((task) => {
      if (task.status === UPLOAD_STATUS.UPLOADING || task.status === UPLOAD_STATUS.PENDING) {
        console.log(`[SecureUpload] Auto-pausing upload ${task.id} due to network loss`);
        task.status = UPLOAD_STATUS.NETWORK_ERROR;
        task.isPaused = true;
        this.abortActiveXhr(task);
      }
    });
    /* eslint-enable no-param-reassign */
    this.notifyListeners();
  }

  async checkNetworkStatus() {
    if (!navigator.onLine) {
      if (this.networkStatus === 'online') {
        this.handleOffline();
      }
      return;
    }

    if (this.networkStatus === 'offline' && navigator.onLine) {
      this.handleOnline();
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      await fetch(UPLOAD_CONFIG.NETWORK_CHECK_URL, {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      this.consecutiveNetworkFailures = 0;
      if (this.networkStatus === 'offline') {
        this.handleOnline();
      }
    } catch (error) {
      this.consecutiveNetworkFailures += 1;
      if (this.consecutiveNetworkFailures >= 3 && !navigator.onLine) {
        if (this.networkStatus === 'online') {
          this.handleOffline();
        }
      }
    }
  }

  /**
   * Subscribe to upload updates
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Current state snapshot, for components that mount mid-upload.
   * `subscribe` only fires on the NEXT change, so a panel mounted after a route
   * change (e.g. the global UploadPanel once the user leaves /gift-content)
   * would render nothing until the next progress tick. Seeding from this makes
   * it appear immediately.
   */
  getSnapshot() {
    return this.buildListenerPayload();
  }

  /**
   * Build the snapshot delivered to listeners.
   */
  buildListenerPayload() {
    const uploadList = Array.from(this.uploads.values()).map((task) => ({
      id: task.id,
      draftId: task.draftId,
      uploadType: task.uploadType || 'video',
      fileName: task.fileName,
      file: task.file,
      status: task.status,
      progress: task.progress,
      bytesUploaded: task.bytesUploaded,
      totalBytes: task.totalBytes,
      uploadedPhotos: task.uploadedPhotos,
      totalPhotos: task.totalPhotos,
      speed: task.speed,
      timeRemaining: task.timeRemaining,
      error: task.error,
      isPaused: task.isPaused,
      metadata: task.metadata,
      s3Key: task.s3Key,
      fileUrl: task.fileUrl,
      thumbnails: task.thumbnails,
      thumbnail: task.thumbnail,
      giftUuid: task.giftUuid,
    }));
    return { uploads: uploadList, networkStatus: this.networkStatus };
  }

  emitNow() {
    this.notifyLastEmit = Date.now();
    const payload = this.buildListenerPayload();
    this.listeners.forEach((callback) => callback(payload));
  }

  /**
   * Notify all listeners of state changes — throttled (leading + trailing edge)
   * so a flood of progress events coalesces into at most one emit per window.
   * Status transitions hit the leading edge immediately; sustained progress
   * keeps emitting on the trailing edge.
   */
  notifyListeners() {
    const now = Date.now();
    const elapsed = now - this.notifyLastEmit;

    if (elapsed >= this.notifyDelay) {
      // Leading edge: emit immediately and clear any pending trailing emit.
      if (this.notifyTimer) {
        clearTimeout(this.notifyTimer);
        this.notifyTimer = null;
      }
      this.emitNow();
      return;
    }

    // Within the window — schedule a trailing emit if one isn't already queued.
    if (this.notifyTimer) return;
    this.notifyTimer = setTimeout(() => {
      this.notifyTimer = null;
      this.emitNow();
    }, this.notifyDelay - elapsed);
  }

  /**
   * Create a photo album upload task (external upload, just tracking)
   */
  createPhotoUpload(options) {
    const {
      albumId,
      albumTitle,
      photoCount,
      updateUploadPercentages,
    } = options;

    // Onboarding checklist — photos count as Upload too.
    markSectionExplored(EXPLORED_SECTION.upload);

    const uploadId = albumId;

    const task = {
      id: uploadId,
      draftId: albumId,
      uploadType: 'photo',
      albumTitle,
      fileName: albumTitle || 'Photo Album',
      totalPhotos: photoCount,
      uploadedPhotos: 0,
      progress: 0,
      speed: 0,
      timeRemaining: 0,
      status: UPLOAD_STATUS.UPLOADING,
      isPaused: false,
      error: null,
      updateUploadPercentages,
      startTime: Date.now(),
      lastUpdateTime: Date.now(),
    };

    this.uploads.set(uploadId, task);

    if (updateUploadPercentages) {
      updateUploadPercentages(albumId, `0/${photoCount}`);
    }

    this.notifyListeners();

    return uploadId;
  }

  /**
   * Update photo upload progress
   */
  updatePhotoUploadProgress(uploadId, uploadedCount, totalCount) {
    const task = this.uploads.get(uploadId);
    if (!task || task.uploadType !== 'photo') return;

    const updatedTask = {
      ...task,
      uploadedPhotos: uploadedCount,
      totalPhotos: totalCount,
      progress: Math.round((uploadedCount / totalCount) * 100),
      lastUpdateTime: Date.now(),
    };

    this.uploads.set(uploadId, updatedTask);

    if (updatedTask.updateUploadPercentages) {
      updatedTask.updateUploadPercentages(uploadId, `${uploadedCount}/${totalCount}`);
    }

    this.notifyListeners();
  }

  /**
   * Complete photo upload
   */
  completePhotoUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task || task.uploadType !== 'photo') return;

    const updatedTask = {
      ...task,
      status: UPLOAD_STATUS.COMPLETED,
      progress: 100,
    };

    this.uploads.set(uploadId, updatedTask);

    if (updatedTask.updateUploadPercentages) {
      updatedTask.updateUploadPercentages(uploadId, null);
    }

    this.notifyListeners();

    if (UPLOAD_CONFIG.AUTO_HIDE_COMPLETED) {
      setTimeout(() => {
        this.removeUpload(uploadId);
      }, UPLOAD_CONFIG.AUTO_HIDE_DELAY);
    }
  }

  /**
   * Fail photo upload
   */
  failPhotoUpload(uploadId, errorMessage) {
    const task = this.uploads.get(uploadId);
    if (!task || task.uploadType !== 'photo') return;

    task.status = UPLOAD_STATUS.FAILED;
    task.error = errorMessage || 'Upload failed';

    if (task.updateUploadPercentages) {
      task.updateUploadPercentages(uploadId, null);
    }

    this.notifyListeners();
  }

  /**
   * Create and start a new upload
   */
  async createUpload(options) {
    const {
      userId,
      file,
      draftId,
      key, // Cloud uploads pass this (old S3 key format)
      metadata,
      thumbnails,
      onProgress,
      onSuccess,
      onError,
      updateUploadPercentages,
      updateUploadDetails,
      setUploadContainer,
      setUploadContainer2,
      updateDummy,
      dummyData,
      suppressToast,
      giftUuid, // Gift flow: use gift APIs (no auth token), skip registerDocument/onSuccess
    } = options;

    // Onboarding checklist — the user has now used Upload. Gift-flow uploads run
    // unauthenticated on someone else's behalf, so they don't count.
    if (!giftUuid) markSectionExplored(EXPLORED_SECTION.upload);

    const uploadId = draftId;

    // Check if we're at the concurrent upload limit
    const activeCount = this.getActiveUploadCount();
    const shouldQueue = activeCount >= UPLOAD_CONFIG.MAX_CONCURRENT_UPLOADS;

    const task = {
      id: uploadId,
      draftId,
      uploadType: 'video',
      userId,
      file,
      fileName: file.name, // Always use original file name for display
      s3KeyFromCaller: key, // Store passed key for cloud uploads if needed
      thumbnails, // Store for later upload
      totalBytes: file.size,
      bytesUploaded: 0,
      progress: 0,
      speed: 0,
      smoothedSpeed: 0,
      timeRemaining: 0,
      status: shouldQueue ? UPLOAD_STATUS.PENDING : UPLOAD_STATUS.INITIALIZING,
      isPaused: false,
      error: null,
      metadata,
      onProgress,
      onSuccess,
      onError,
      updateUploadPercentages,
      updateUploadDetails,
      setUploadContainer,
      setUploadContainer2,
      updateDummy,
      dummyData,
      // Upload state
      sessionId: null,
      s3Key: null,
      thumbnailKeys: [],
      presignedUrls: [],
      parts: [],
      currentPartNumber: 0,
      startTime: Date.now(),
      lastUpdateTime: Date.now(),
      lastSpeedUpdateTime: Date.now(),
      lastBytesUploaded: 0,
      retryCount: 0,
      uploadMode: 'presigned-url', // Identifier for resume logic
      suppressToast: !!suppressToast, // Gift flow has its own progress UI
      giftUuid: giftUuid || null, // Gift flow: UUID-based auth, no token needed
    };

    this.uploads.set(uploadId, task);

    // Add to upload containers
    if (setUploadContainer) {
      setUploadContainer((prev) => [...prev, draftId]);
    }
    if (setUploadContainer2) {
      setUploadContainer2((prev) => [...prev, draftId]);
    }

    // Create dummy if provided
    if (dummyData && updateDummy) {
      const dummy = createDummy({
        ...dummyData,
        draft_id: draftId,
      });
      updateDummy(dummy, 1);
    }

    // Show toast notification (suppressed for gift flow which has its own progress UI)
    if (!task.suppressToast) {
      const trimmedFilename = file.name.length > 26 ? `${file.name.substring(0, 26)}...` : file.name;
      toast(trimmedFilename, {
        autoClose: false,
        toastId: draftId,
        position: toast.POSITION.BOTTOM_RIGHT,
        closeButton: false,
      });
    }

    // Update status
    if (updateUploadPercentages) {
      if (shouldQueue) {
        updateUploadPercentages(draftId, 'In queue...');
      } else {
        updateUploadPercentages(draftId, 'Initializing');
      }
    }

    this.notifyListeners();

    // Start upload immediately if not queued
    if (!shouldQueue) {
      this.startUpload(uploadId);
    }

    return uploadId;
  }

  /**
   * Start or resume upload
   */
  async startUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task) return;
    // Guard against a second concurrent loop for the same task (e.g. a resume
    // firing while the previous loop is still unwinding). The finally clears it.
    if (task.isRunning) return;
    task.isRunning = true;

    try {
      // Check network
      if (this.networkStatus === 'offline') {
        task.status = UPLOAD_STATUS.NETWORK_ERROR;
        task.isPaused = true;
        this.notifyListeners();
        return;
      }

      task.status = UPLOAD_STATUS.UPLOADING;
      task.isPaused = false;

      if (!task.startTime || task.startTime === 0) {
        task.startTime = Date.now();
      }

      task.lastUpdateTime = Date.now();
      task.lastSpeedUpdateTime = Date.now();
      task.lastBytesUploaded = task.bytesUploaded;
      task.smoothedSpeed = 0;

      this.notifyListeners();

      // STEP 1: Skip separate thumbnail upload - thumbnails will be sent as binary in API call
      // This matches the old flow and is simpler (one less upload phase)
      console.log('[SecureUpload] Thumbnails will be sent as binary data in final API call');

      // STEP 2: Create upload session (if not already done)
      if (!task.sessionId) {
        let session;

        if (task.giftUuid) {
          // Gift flow: use gift API with UUID, server generates S3 key
          console.log(`[SecureUpload] Creating GIFT upload session for ${task.fileName} (uuid: ${task.giftUuid})`);

          session = await createGiftUploadSession(task.giftUuid, {
            file_name: task.fileName,
            file_size: task.file.size,
            content_type: task.file.type || 'video/mp4',
          });

          // Server returns the S3 key
          task.s3Key = session.s3_key || session.file_key || null;
        } else {
          // Normal flow: generate S3 key locally, use token-based API
          task.s3Key = generateSecureS3Key(task.userId, task.fileName);

          console.log(`[SecureUpload] Creating upload session for ${task.fileName}`);
          console.log(`[SecureUpload] S3 Key: ${task.s3Key}`);

          session = await createUploadSession({
            file_name: task.fileName,
            file_size: task.file.size,
            content_type: task.file.type || 'video/mp4',
            s3_key: task.s3Key,
          });
        }

        task.sessionId = session.id;
        task.presignedUrls = session.presigned_urls;
        task.partSize = session.part_size;
        task.totalParts = session.total_parts;

        console.log(`[SecureUpload] Session created: ${task.sessionId}, ${task.totalParts} parts`);
      }

      // Update status
      if (task.updateUploadPercentages) {
        task.updateUploadPercentages(task.draftId, 'Starting Upload');
      }

      // STEP 3: Upload parts using presigned URLs
      for (
        let partNumber = task.currentPartNumber + 1;
        partNumber <= task.totalParts;
        partNumber += 1
      ) {
        // Check if paused or cancelled
        if (task.isPaused || task.status === UPLOAD_STATUS.CANCELLED || task.status === UPLOAD_STATUS.CANCELLING) {
          task.currentPartNumber = partNumber - 1;
          return;
        }

        // Check network
        if (this.networkStatus === 'offline') {
          task.status = UPLOAD_STATUS.NETWORK_ERROR;
          task.isPaused = true;
          task.currentPartNumber = partNumber - 1;
          this.notifyListeners();
          return;
        }

        const start = (partNumber - 1) * task.partSize;
        const end = Math.min(start + task.partSize, task.file.size);
        const partBlob = task.file.slice(start, end);

        // Resolve a fresh (unexpired) presigned URL for this part — refreshing
        // from the server if the cached one is missing or about to expire.
        // `let` because a reactive 403 refresh below re-resolves it.
        let presignedUrl = await this.getFreshPartUrl(task, partNumber);

        if (!presignedUrl) {
          console.error(`[SecureUpload] No presigned URL for part ${partNumber}`);
          throw new Error(`Missing presigned URL for part ${partNumber}`);
        }

        // One reactive URL refresh per part if S3 rejects with an expiry-style
        // status, so a resume that just missed the expiry margin still recovers.
        let urlRefreshedReactively = false;

        // Upload part with retry
        let uploadSuccess = false;
        let retryCount = 0;

        while (!uploadSuccess && retryCount < UPLOAD_CONFIG.RETRY_ATTEMPTS) {
          try {
            const bytesBeforePart = start;

            // Create XHR ref so cancelUpload can abort the active request
            const xhrRef = { current: null };
            task.activeXhrRef = xhrRef;

            const part = await uploadPartWithPresignedUrl(
              presignedUrl,
              partBlob,
              partNumber,
              (loaded, total) => {
                const currentPartUploaded = loaded || 0;
                let totalUploaded = bytesBeforePart + currentPartUploaded;
                totalUploaded = Math.max(totalUploaded, task.bytesUploaded);

                task.bytesUploaded = totalUploaded;
                task.progress = Math.min(
                  99,
                  Math.round((totalUploaded / task.totalBytes) * 100)
                );

                const currentTime = Date.now();
                const timeDiffProgress = (currentTime - task.lastUpdateTime) / 1000;
                const timeDiffSpeed = (currentTime - task.lastSpeedUpdateTime) / 1000;

                const shouldUpdateProgress = timeDiffProgress >= UPLOAD_CONFIG.PROGRESS_UPDATE_INTERVAL / 1000;
                const shouldUpdateSpeed = timeDiffSpeed >= UPLOAD_CONFIG.SPEED_UPDATE_INTERVAL / 1000;

                if (shouldUpdateSpeed) {
                  const bytesDiff = totalUploaded - task.lastBytesUploaded;

                  if (bytesDiff > 0) {
                    const instantSpeed = bytesDiff / timeDiffSpeed;
                    const alpha = 0.3;
                    task.smoothedSpeed = task.smoothedSpeed === 0
                      ? instantSpeed
                      : alpha * instantSpeed + (1 - alpha) * task.smoothedSpeed;

                    task.speed = task.smoothedSpeed;
                    task.timeRemaining = (task.totalBytes - totalUploaded) / task.speed;
                  }

                  task.lastSpeedUpdateTime = currentTime;
                  task.lastBytesUploaded = totalUploaded;
                }

                if (shouldUpdateProgress) {
                  task.lastUpdateTime = currentTime;

                  if (task.updateUploadPercentages) {
                    task.updateUploadPercentages(task.draftId, task.progress);
                  }

                  if (!task.suppressToast) {
                    const trimmedFilename = task.fileName.length > 26
                      ? `${task.fileName.substring(0, 26)}...`
                      : task.fileName;
                    toast.update(task.draftId, {
                      render: `${trimmedFilename} ${task.progress}%`,
                      autoClose: false,
                    });
                  }

                  if (task.onProgress) {
                    task.onProgress(task.progress, totalUploaded, task.totalBytes);
                  }

                  this.notifyListeners();
                }

                if (shouldUpdateSpeed && task.updateUploadDetails) {
                  const elapsedSize = totalUploaded / 1024 / 1024;
                  const elapsedTime = (currentTime - task.startTime) / 1000;
                  const remainingSize = (task.totalBytes - totalUploaded) / 1024 / 1024;

                  task.updateUploadDetails(task.draftId, {
                    elapsed_size: elapsedSize,
                    elapsed_time: elapsedTime,
                    remaining_size: remainingSize > 0 ? remainingSize : 0,
                    speed: task.speed,
                    time_remaining: task.timeRemaining,
                  });
                }
              },
              xhrRef,
              UPLOAD_CONFIG.PART_STALL_TIMEOUT
            );

            task.activeXhrRef = null;
            task.parts.push(part);
            task.currentPartNumber = partNumber;
            this.notifyListeners();
            uploadSuccess = true;
          } catch (error) {
            task.activeXhrRef = null;

            // Interrupted by an explicit pause / cancel → stop cleanly and
            // keep this part pending so a resume redoes it. Not a failure.
            if (
              task.isPaused
              || task.status === UPLOAD_STATUS.PAUSED
              || task.status === UPLOAD_STATUS.CANCELLED
              || task.status === UPLOAD_STATUS.CANCELLING
            ) {
              task.currentPartNumber = partNumber - 1;
              this.notifyListeners();
              return;
            }

            // Network dropped (offline event, or an aborted/stalled request
            // while we're offline) → park as a recoverable NETWORK_ERROR so the
            // online / foreground handlers resume it. Don't burn the retry
            // budget or fall through to the hard-failure path.
            if (this.networkStatus === 'offline' || !navigator.onLine) {
              task.status = UPLOAD_STATUS.NETWORK_ERROR;
              task.isPaused = true;
              task.currentPartNumber = partNumber - 1;
              if (task.updateUploadPercentages) {
                task.updateUploadPercentages(task.draftId, 'Waiting for network');
              }
              this.notifyListeners();
              return;
            }

            // Expired / rejected presigned URL (S3 returns 403 or 400) →
            // refresh the URL once and retry immediately WITHOUT burning the
            // retry budget. This is the common Android-after-lock failure: the
            // tab was frozen long enough that the URL expired.
            const msg = (error && error.message) || '';
            if (/status 40[0-3]\b/.test(msg) && !urlRefreshedReactively) {
              try {
                await this.refreshTaskUrls(task, partNumber);
                const refreshed = await this.getFreshPartUrl(task, partNumber);
                if (refreshed) {
                  presignedUrl = refreshed;
                  urlRefreshedReactively = true;
                  // eslint-disable-next-line no-continue
                  continue;
                }
              } catch (refreshErr) {
                console.error('[SecureUpload] Presigned URL refresh failed:', refreshErr);
              }
            }

            retryCount += 1;
            console.error(`[SecureUpload] Error uploading part ${partNumber}, retry ${retryCount}:`, error);

            if (retryCount >= UPLOAD_CONFIG.RETRY_ATTEMPTS) {
              throw error;
            }

            await new Promise((resolve) => setTimeout(resolve, UPLOAD_CONFIG.RETRY_DELAY));
          }
        }
      }

      // STEP 4: Complete multipart upload
      const sortedParts = [...task.parts].sort((a, b) => a.PartNumber - b.PartNumber);

      console.log(`[SecureUpload] Completing upload session for ${task.fileName}`);

      if (task.giftUuid) {
        const completeResponse = await completeGiftUploadSession(task.sessionId, task.giftUuid, sortedParts);
        // Server may return file_url or s3_key in completion response
        if (completeResponse?.file_url) {
          task.fileUrl = completeResponse.file_url;
        }
        if (completeResponse?.s3_key || completeResponse?.file_key) {
          task.s3Key = completeResponse.s3_key || completeResponse.file_key;
        }
      } else {
        await completeUploadSession(task.sessionId, sortedParts);
      }

      // Gift flow: S3 upload done — skip registerDocument and onSuccess (need token after payment)
      if (task.giftUuid) {
        task.status = UPLOAD_STATUS.S3_COMPLETE;
        task.progress = 100;
        this.notifyListeners();

        console.log(`[SecureUpload] Gift upload S3 complete for ${task.fileName}, awaiting payment for registration`);

        if (task.updateUploadPercentages) {
          task.updateUploadPercentages(task.draftId, 'Uploaded');
        }

        if (task.onProgress) {
          task.onProgress(100, task.totalBytes, task.totalBytes);
        }

        // Start next upload in queue
        this.startNextInQueue();
        return;
      }

      // STEP 5: Register document (normal flow only — not gift)
      task.status = UPLOAD_STATUS.FINISHING;
      task.progress = 100;
      this.notifyListeners();

      if (task.updateUploadPercentages) {
        task.updateUploadPercentages(task.draftId, 'Please wait');
      }

      if (!task.suppressToast) {
        toast.update(task.draftId, {
          render: 'Finalizing upload...',
          type: toast.TYPE.INFO,
          autoClose: false,
          closeButton: false,
        });
      }

      console.log(`[SecureUpload] Registering document for ${task.fileName}`);

      // Use video URL as thumbnail (thumbnails will be sent as binary in addVideo API)
      const documentResponse = await registerDocument({
        name: task.fileName,
        url: task.s3Key,
        thumbnail: task.s3Key, // Use video URL as thumbnail placeholder
        thumbnails: [], // Empty array - thumbnails sent as binary in addVideo API
        uploaded_at: new Date().toISOString(),
      });

      task.documentId = documentResponse.id;

      console.log(`[SecureUpload] Document registered: ${task.documentId}`);

      // STEP 6: Call success callback (adds video to backend + links draft)
      if (task.onSuccess) {
        await task.onSuccess(task.draftId, {
          s3Key: task.s3Key,
          thumbnails: task.thumbnails, // Pass binary thumbnails to API call
          documentId: task.documentId,
        });
      }

      // Upload complete
      task.status = UPLOAD_STATUS.COMPLETED;
      this.notifyListeners();

      if (!task.suppressToast) {
        toast.update(task.draftId, {
          render: 'Upload completed!',
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
          closeButton: false,
        });
      }

      // Remove from upload containers
      if (task.setUploadContainer) {
        task.setUploadContainer((prev) => prev.filter((id) => id !== task.draftId));
      }
      if (task.setUploadContainer2) {
        task.setUploadContainer2((prev) => prev.filter((id) => id !== task.draftId));
      }

      // Auto-remove completed upload after delay
      if (UPLOAD_CONFIG.AUTO_HIDE_COMPLETED) {
        setTimeout(() => {
          this.removeUpload(uploadId);
        }, UPLOAD_CONFIG.AUTO_HIDE_DELAY);
      }

      // Start next upload in queue
      this.startNextInQueue();
    } catch (error) {
      task.activeXhrRef = null;

      // Don't overwrite CANCELLED/CANCELLING status with FAILED
      if (task.status === UPLOAD_STATUS.CANCELLED || task.status === UPLOAD_STATUS.CANCELLING) {
        console.log('[SecureUpload] Upload was cancelled, ignoring error:', error.message);
        return;
      }

      // Offline / device-lock interruption (e.g. session-create or complete
      // call failed because the network vanished) → treat as recoverable, not
      // a hard failure, so the online / foreground handlers resume it.
      if (this.networkStatus === 'offline' || !navigator.onLine || task.isPaused) {
        console.log('[SecureUpload] Upload interrupted by network — parking as recoverable');
        task.status = UPLOAD_STATUS.NETWORK_ERROR;
        task.isPaused = true;
        if (task.updateUploadPercentages) {
          task.updateUploadPercentages(task.draftId, 'Waiting for network');
        }
        this.notifyListeners();
        return;
      }

      console.error('[SecureUpload] Upload error:', error);

      logAppEvent({
        userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: task.giftUuid ? 'Gift Upload' : 'Add Video',
        description: task.giftUuid
          ? `Gift upload failed — file: "${task.fileName || 'unknown'}", giftUuid: ${task.giftUuid}, progress: ${task.progress || 0}%, reason: ${error.message || 'Unknown error'}`
          : `Video upload failed — file: "${task.fileName || 'unknown'}", progress: ${task.progress || 0}%, reason: ${error.message || 'Unknown error'}`,
        severity: 'high',
        errorMessage: error.message || 'Upload pipeline failed',
        errorStack: error.stack || null,
        component: task.giftUuid ? 'giftFlowUpload' : 'uploadManagerSecure',
        userAction: task.giftUuid
          ? `gift upload: ${task.fileName || ''}`
          : `uploading video: ${task.fileName || ''}`,
      });

      task.status = UPLOAD_STATUS.FAILED;
      task.error = error.message || 'Upload failed';

      if (task.updateUploadPercentages) {
        task.updateUploadPercentages(task.draftId, 'Failed');
      }

      if (!task.suppressToast) {
        toast.update(task.draftId, {
          render: 'Upload failed',
          type: toast.TYPE.ERROR,
          autoClose: 3000,
          closeButton: false,
        });
      }

      // Remove from upload containers
      if (task.setUploadContainer) {
        task.setUploadContainer((prev) => prev.filter((id) => id !== task.draftId));
      }
      if (task.setUploadContainer2) {
        task.setUploadContainer2((prev) => prev.filter((id) => id !== task.draftId));
      }

      if (task.onError) {
        task.onError(error, task.draftId);
      }

      this.notifyListeners();

      // Start next upload in queue
      this.startNextInQueue();
    } finally {
      // Always release the concurrency guard so a later resume/recover can
      // re-enter this task's upload loop.
      task.isRunning = false;
    }
  }

  /**
   * Pause upload
   */
  pauseUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task) return;

    if (task.status === UPLOAD_STATUS.UPLOADING) {
      task.status = UPLOAD_STATUS.PAUSED;
      task.isPaused = true;
      // Abort the in-flight part so the loop unwinds now instead of after the
      // current (possibly large) part finishes.
      this.abortActiveXhr(task);
      this.notifyListeners();

      // Start next upload in queue
      this.startNextInQueue();
    }
  }

  /**
   * Resume upload
   */
  resumeUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task) return;

    if (
      task.status === UPLOAD_STATUS.PAUSED
      || task.status === UPLOAD_STATUS.NETWORK_ERROR
    ) {
      if (this.networkStatus === 'offline') {
        if (!task.suppressToast) {
          toast.error('Cannot resume: No network connection');
        }
        return;
      }

      task.status = UPLOAD_STATUS.UPLOADING;
      task.isPaused = false;
      this.startUpload(uploadId);
    }
  }

  /**
   * Cancel upload
   */
  async cancelUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task) return;

    task.status = UPLOAD_STATUS.CANCELLING;
    task.isPaused = true;
    this.notifyListeners();

    // Abort the active XHR request if one is in progress
    if (task.activeXhrRef?.current) {
      try {
        task.activeXhrRef.current.abort();
      } catch (e) {
        console.warn('[SecureUpload] Error aborting XHR:', e);
      }
      task.activeXhrRef = null;
    }

    task.status = UPLOAD_STATUS.CANCELLED;

    if (task.updateUploadPercentages) {
      task.updateUploadPercentages(task.draftId, 'Cancelled');
    }

    if (!task.suppressToast) {
      toast.update(task.draftId, {
        render: 'Upload cancelled',
        type: toast.TYPE.ERROR,
        autoClose: 3000,
        closeButton: false,
      });
    }

    // Remove from upload containers
    if (task.setUploadContainer) {
      task.setUploadContainer((prev) => prev.filter((id) => id !== task.draftId));
    }
    if (task.setUploadContainer2) {
      task.setUploadContainer2((prev) => prev.filter((id) => id !== task.draftId));
    }

    if (task.updateDummy) {
      task.updateDummy(task.draftId, 0);
    }

    this.notifyListeners();

    setTimeout(() => {
      this.uploads.delete(uploadId);
      this.notifyListeners();
      this.startNextInQueue();
    }, 500);
  }

  /**
   * Remove upload from manager
   */
  removeUpload(uploadId) {
    this.uploads.delete(uploadId);
    this.notifyListeners();
  }

  /**
   * Get all uploads
   */
  getAllUploads() {
    return Array.from(this.uploads.values()).map((task) => ({
      id: task.id,
      draftId: task.draftId,
      fileName: task.fileName,
      status: task.status,
      progress: task.progress,
      bytesUploaded: task.bytesUploaded,
      totalBytes: task.totalBytes,
      speed: task.speed,
      timeRemaining: task.timeRemaining,
      error: task.error,
      isPaused: task.isPaused,
      metadata: task.metadata,
    }));
  }

  /**
   * Get upload by ID
   */
  getUpload(uploadId) {
    return this.uploads.get(uploadId);
  }

  /**
   * Get all gift uploads that finished S3 upload but need registration after payment
   */
  getUploadStatus(uploadId) {
    const task = this.uploads.get(uploadId);
    return task ? task.status : null;
  }

  getS3CompleteUploads() {
    const results = [];
    this.uploads.forEach((task) => {
      if (task.status === UPLOAD_STATUS.S3_COMPLETE && task.giftUuid) {
        results.push({
          id: task.id,
          draftId: task.draftId,
          fileName: task.fileName,
          s3Key: task.s3Key,
          fileUrl: task.fileUrl,
          thumbnails: task.thumbnails,
          file: task.file,
        });
      }
    });
    return results;
  }

  /**
   * Mark a gift upload as fully completed after post-payment registration
   */
  markUploadCompleted(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task) return;

    task.status = UPLOAD_STATUS.COMPLETED;
    this.notifyListeners();

    // Remove from upload containers
    if (task.setUploadContainer) {
      task.setUploadContainer((prev) => prev.filter((id) => id !== task.draftId));
    }
    if (task.setUploadContainer2) {
      task.setUploadContainer2((prev) => prev.filter((id) => id !== task.draftId));
    }

    if (UPLOAD_CONFIG.AUTO_HIDE_COMPLETED) {
      setTimeout(() => {
        this.removeUpload(uploadId);
      }, UPLOAD_CONFIG.AUTO_HIDE_DELAY);
    }
  }

  // ============================================================
  // Remote Upload (Mobile Upload via SSE)
  // ============================================================

  /**
   * Create a remote upload entry (tracking-only, no local file).
   * Used when mobile device is uploading and we just track progress on desktop.
   */
  createRemoteUpload({ id, fileName, totalBytes = 0, thumbnail = null }) {
    const task = {
      id,
      draftId: id,
      uploadType: 'remote',
      fileName,
      progress: 0,
      bytesUploaded: 0,
      totalBytes,
      speed: 0,
      timeRemaining: 0,
      status: UPLOAD_STATUS.UPLOADING,
      isPaused: false,
      error: null,
      startTime: Date.now(),
      lastUpdateTime: Date.now(),
      thumbnail,
    };

    this.uploads.set(id, task);
    this.notifyListeners();
    return id;
  }

  /**
   * Update a remote upload entry with new progress/status from SSE.
   */
  updateRemoteUpload(id, updates) {
    const task = this.uploads.get(id);
    if (!task || task.uploadType !== 'remote') return;

    const updatedTask = {
      ...task,
      ...updates,
      lastUpdateTime: Date.now(),
    };

    this.uploads.set(id, updatedTask);
    this.notifyListeners();
  }

  /**
   * Mark a remote upload as completed.
   */
  completeRemoteUpload(id, extras = {}) {
    const task = this.uploads.get(id);
    if (!task || task.uploadType !== 'remote') return;

    const updatedTask = {
      ...task,
      ...extras,
      status: UPLOAD_STATUS.COMPLETED,
      progress: 100,
    };

    this.uploads.set(id, updatedTask);
    this.notifyListeners();

    // Auto-remove after delay, same as local uploads
    if (UPLOAD_CONFIG.AUTO_HIDE_COMPLETED) {
      setTimeout(() => {
        this.removeUpload(id);
      }, UPLOAD_CONFIG.AUTO_HIDE_DELAY);
    }
  }

  /**
   * Cleanup
   */
  destroy() {
    clearInterval(this.networkCheckInterval);
    if (this.notifyTimer) {
      clearTimeout(this.notifyTimer);
      this.notifyTimer = null;
    }
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));
    this.uploads.clear();
    this.listeners.clear();
  }
}

// Create singleton instance
const uploadManagerSecure = new UploadManagerSecure();

export default uploadManagerSecure;

/**
 * Wrapper function for compatibility with existing code
 */
export async function uploadLargeFileSecure(options) {
  return uploadManagerSecure.createUpload(options);
}
